import * as React from 'react';
import {
    Container, Box, Paper, TextField, InputAdornment, Button,
    Stack, IconButton, CircularProgress, Typography, Pagination, MenuItem, Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';

import dayjs from 'dayjs';
import { ordenesProveedor } from '../Componentes/FunctionLogin.js';
import { useTranslation } from 'react-i18next';
import OrderCard from './OrderCard';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
const XLSX = require('xlsx');

export default function OrdersList() {
    const [t] = useTranslation("global");

    // State
    const [searchTerm, setSearchTerm] = React.useState('');
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [filterState, setFilterState] = React.useState(" and CITM_LFCYCLE_ST eq '6' ");
    // Inicializar fechas con dayjs para compatibilidad con MUI DatePicker
    const [filterDateFrom, setFilterDateFrom] = React.useState(() => {
        return dayjs().subtract(90, 'day');
    });
    const [filterDateTo, setFilterDateTo] = React.useState(() => dayjs());
    const [filteredOrders, setFilteredOrders] = React.useState([]);
    const ordersPerPage = 10;

    // Load orders function
    const loadOrders = async () => {
        setLoading(true);
        try {
            // Siempre enviar fechas en formato YYYY-MM-DD usando dayjs
            const fechaDesde = filterDateFrom && typeof filterDateFrom.format === 'function'
                ? filterDateFrom.format('YYYY-MM-DD')
                : dayjs().startOf('month').format('YYYY-MM-DD');
            const fechaHasta = filterDateTo && typeof filterDateTo.format === 'function'
                ? filterDateTo.format('YYYY-MM-DD')
                : dayjs().format('YYYY-MM-DD');

            const result = await ordenesProveedor(
                fechaDesde,
                fechaHasta,
                filterState || "",
                "", // sede
                ""  // orden
            );
            setOrders(result);
            setFilteredOrders(result); // Aplica todos los filtros por defecto
        } catch (error) {
            console.error('Error loading orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredOrders);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Órdenes');
        XLSX.writeFile(workbook, `ordenes_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Combine search and filters


    // Pagination
    const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
    const paginatedOrders = filteredOrders.slice(
        (page - 1) * ordersPerPage,
        page * ordersPerPage
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Search and Actions Bar */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <TextField
                        placeholder={t("ordenes.buscar") || "Buscar órdenes..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                loadOrders();
                            }
                        }}
                        sx={{ flexGrow: 1, minWidth: '250px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        variant="contained"
                        onClick={loadOrders}
                        sx={{
                            backgroundColor: '#BD1F34',
                            '&:hover': { backgroundColor: '#9a1829' }
                        }}
                    >
                        {t("ordenes.buscar") || "Buscar"}
                    </Button>

                    <IconButton
                        color="primary"
                        onClick={loadOrders}
                        sx={{ backgroundColor: '#F3F4F6' }}
                    >
                        <RefreshIcon />
                    </IconButton>

                    <IconButton
                        color="primary"
                        onClick={handleExportExcel}
                        disabled={filteredOrders.length === 0}
                        sx={{ backgroundColor: '#F3F4F6' }}
                    >
                        <FileDownloadIcon />
                    </IconButton>
                </Stack>
            </Paper>

            {/* Filters Section */}
            {/* Filters Section */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#F9FAFB' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
                    {t("ordenes.filtros_titulo") || "FILTROS DE BÚSQUEDA"}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label={t("ordenes.estado_label") || "Estado de la Orden"}
                            value={filterState}
                            onChange={(e) => setFilterState(e.target.value)}
                        >
                            <MenuItem value="">{t("ordenes.totalestados") || "Todos los estados"}</MenuItem>
                            <MenuItem value=" and CITM_LFCYCLE_ST eq '6' ">{t("ordenes.pendientedeentrega") || "Pendiente de entrega"}</MenuItem>
                            <MenuItem value=" and CITM_LFCYCLE_ST eq '9' ">{t("ordenes.entregado") || "Entregado"}</MenuItem>
                            <MenuItem value=" and CITM_LFCYCLE_ST eq '10' ">{t("ordenes.facturacontabilizada") || "Factura Contabilizada"}</MenuItem>
                            <MenuItem value=" and CITM_LFCYCLE_ST eq '8' ">{t("ordenes.cancelado") || "Cancelado"}</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack direction="row" spacing={2}>
                                <DatePicker
                                    label={t("ordenes.fecha_desde") || "Fecha desde"}
                                    value={filterDateFrom}
                                    onChange={(newValue) => setFilterDateFrom(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                                <DatePicker
                                    label={t("ordenes.fecha_hasta") || "Fecha hasta"}
                                    value={filterDateTo}
                                    onChange={(newValue) => setFilterDateTo(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Stack>
                        </LocalizationProvider>
                    </Grid>
                </Grid>
            </Paper>

            {/* Orders List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} sx={{ color: '#BD1F34' }} />
                </Box>
            ) : paginatedOrders.length > 0 ? (
                <Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
                        {t("ordenes.mostrando", { count: paginatedOrders.length, total: filteredOrders.length }) || `Mostrando ${paginatedOrders.length} de ${filteredOrders.length} órdenes`}
                    </Typography>

                    <Stack spacing={2}>
                        {paginatedOrders.map((order, index) => (
                            <OrderCard key={order.id || index} order={order} />
                        ))}
                    </Stack>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </Box>
            ) : orders.length === 0 && !loading ? null : (
                <Typography variant="body2" sx={{ textAlign: 'center', color: '#6B7280' }}>
                    {t("ordenes.no_encontradas") || "No se encontraron órdenes."}
                </Typography>
            )}
        </Container>
    );
}